import React, { useEffect, useState, useContext } from 'react';
import { WebSocketContext } from '../WebSocketContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Refresh, TrendingUp, TrendingDown, AccountBalance, Description, Close, Search } from '@mui/icons-material';
import BottomSheetClosePosition from '../components/BottomSheetClosePosition';
import './../NetPositions.css';
import config from '../config';

const NetPositions = () => {
  const { userDetails, stocks, netPositions, summary, refreshTradesAndPortfolio } = useContext(WebSocketContext);
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isClosingAll, setIsClosingAll] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle navigation state from StockCard
  useEffect(() => {
    if (location.state?.filterByScript) {
      setSearchQuery(location.state.filterByScript);
    }
  }, [location.state]);

  // const fetchNetPositions = async () => {
  //   try {
  //     setLoading(true);
  //     setError(null);
      
  //     const response = await fetch(`${config.API_BASE_URL}getNetPositionsMain`, {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //       },
  //       body: JSON.stringify({
  //         clientId: userDetails.clientId
  //       }),
  //     });
      
  //     const data = await response.json();
      
  //     if (data.success) {
  //       // console.log(data.positions);
  //       setNetPositions(data.positions);
  //       setSummary(data.summary);
  //     } else {
  //       setError(data.message || 'Failed to fetch net positions');
  //     }
  //   } catch (error) {
  //     console.error('Error fetching net positions:', error);
  //     setError('Network error occurred');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // useEffect(() => {
  //   if (userDetails.clientId) {
  //     fetchNetPositions();
  //   }
  // }, [userDetails.clientId]);

  const handleRefresh = () => {
    // fetchNetPositions();
    refreshTradesAndPortfolio("both");
  };

  const handlePositionClick = (position) => {
    // Navigate to position details or open bottom sheet
    console.log('Position clicked:', position);
  };

  const handleTradesClick = (position) => {
    // Navigate to trades page with specific script filter
    navigate('/trades', { 
      state: { 
        filterByScript: position.stockName,
        instrumentToken: position.instrument_token 
      } 
    });
  };

  const handleClosePositionClick = (position) => {
    setSelectedPosition(position);
    setIsBottomSheetOpen(true);
  };

  const handleCloseBottomSheet = () => {
    setIsBottomSheetOpen(false);
    setSelectedPosition(null);
  };

  const handleHeaderToggle = () => {
    setIsHeaderExpanded(!isHeaderExpanded);
  };

  // Close All functionality
  const handleCloseAll = async () => {
    const openPositions = filteredPositions.filter(pos => pos.netQty != 0);
    
    if (openPositions.length === 0) {
      alert('No open positions to close.');
      return;
    }
    
    // Show detailed confirmation
    const totalValue = openPositions.reduce((sum, pos) => {
      const stock = stocks[pos.instrument_token] || {};
      return sum + (Math.abs(pos.netQty) * (pos.buyQty > 0 ? stock.Ask : stock.Bid));
    }, 0);
    
    const confirmMessage = `
      Close All Positions:
      • ${openPositions.length} positions
      • Total Value: ₹${totalValue.toLocaleString('en-IN', {minimumFractionDigits: 2})}
      
      This will create market orders to close all positions.
      Are you sure?
    `;
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      setIsClosingAll(true);
      
      // Create close orders for each position
      const closePromises = openPositions.map(position => {
        const stock = stocks[position.instrument_token] || {};
        const closeOrder = {
          clientId: userDetails.clientId,
          stockId: position.stockId,
          stockName: position.stockName,
          exchange: position.exchange,
          expiry: position.expiry,
          orderQuantity: Math.abs(position.netQty),
          buy_sell: position.buyQty > 0 ? 2 : 1, // Opposite of current position
          orderType: 1, // Market order
          orderPrice: position.buyQty > 0 ? stock.Ask : stock.Bid, // Market price
          brokerage: 0,
          broker_brokerage: JSON.stringify({ master: 0, upline_sharing_amount: 0 })
        };
        
        return fetch(`${config.API_BASE_URL}createOrder`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(closeOrder)
        });
      });
      
      const results = await Promise.allSettled(closePromises);
      
      // Handle results
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;
      
      if (failed > 0) {
        alert(`Close All completed: ${successful} successful, ${failed} failed`);
      } else {
        alert(`All ${successful} positions closed successfully!`);
      }
      
      // Refresh data
      refreshTradesAndPortfolio("both");
      
    } catch (error) {
      console.error('Close All Error:', error);
      alert('Error closing positions. Please try again.');
    } finally {
      setIsClosingAll(false);
    }
  };

  // Filter positions based on search query
  const filteredPositions = netPositions.filter(position => {
    if (!searchQuery) return true;
    
    // Check if searchQuery is from StockCard navigation (exact match)
    if (location.state?.filterByScript) {
      return position.stockName?.toLowerCase() === searchQuery.toLowerCase() ||
             position.dispname?.toLowerCase() === searchQuery.toLowerCase() ||
             position.stockId == searchQuery;
    }
    
    // Regular search (partial match for manual typing)
    return position.stockName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           position.dispname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           position.stockId == searchQuery;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const getPnLColor = (pnl) => {
    if (pnl > 0) return 'positive';
    if (pnl < 0) return 'negative';
    return 'neutral';
  };

  const getPositionSideColor = (side) => {
    switch (side) {
      case 'BUY': return 'buy';
      case 'SELL': return 'sell';
      default: return 'neutral';
    }
  };

  // Calculate PnL function (using live prices from stocksLT)
  const calculatePnL = (position, stock) => {
    if (position.buyQty > 0) {
      
      // Use live bid price from stocksLT, fallback to stock context, then position data
      const currentBid = stock.Bid || position.buyPrice || 0;
      if (currentBid === 0) { 
        return 0;
      } else { 
        // console.log(currentAsk, Number(position.avgBuyPrice), Number(position.netQty));
        return (currentBid * Number(position.netQty)) - (Number(position.avgBuyPrice) * Number(position.netQty));
      }
    } else {
      // Use live ask price from stocksLT, fallback to stock context, then position data
      const currentAsk = stock.Ask || position.sellPrice || 0;
      if (currentAsk === 0) { 
        return 0;
      } else {
        // console.log(Number(position.avgSellPrice), currentBid, Number(position.netQty));
        return (Number(position.avgSellPrice) * Number(position.netQty)) - (currentAsk * Number(position.netQty));
      }
    }
  };

  if (loading) {
    return (
      <div className={`net-positions-page ${isDarkMode ? 'dark-theme' : 'light-theme'}`}>
        <div className="net-pos-loading-container">
          <div className="net-pos-loading-spinner"></div>
          <p>Loading net positions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`net-positions-page ${isDarkMode ? 'dark-theme' : 'light-theme'}`}>
        <div className="net-pos-error-container">
          <div className="net-pos-error-icon">⚠️</div>
          <h3>Error Loading Positions</h3>
          <p>{error}</p>
          <button className="net-pos-retry-btn" onClick={handleRefresh}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`net-positions-page ${isDarkMode ? 'dark-theme' : 'light-theme'}`} data-theme={isDarkMode ? 'dark' : 'light'}>
      {/* Compact Theme Header Section */}
      <div className="net-pos-header-section">
        <div className={`net-pos-header-content ${isHeaderExpanded ? 'expanded' : 'collapsed'}`}>
          <div className="net-pos-summary-info">
            <div className="net-pos-summary-row">
              <span className="net-pos-summary-label">Limit :</span>
              <span className="net-pos-summary-value">{(userDetails.marginAvailable || 0).toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
            </div>
            <div className="net-pos-summary-row">
              <span className="net-pos-summary-label">Net :</span>
              <span className="net-pos-summary-value">
                {(Number(userDetails.marginAvailable || 0) + netPositions.reduce((total, pos) => {
                  const stock = stocks[pos.instrument_token] || {};
                  return total + calculatePnL(pos, stock);
                }, 0)).toLocaleString('en-IN', {minimumFractionDigits: 2})}
              </span>
            </div>
            <div className="net-pos-summary-row">
              <span className="net-pos-summary-label">Current PNL :</span>
              {/* <span className={`net-pos-summary-value ${getPnLColor(
                netPositions.reduce((total, pos) => {
                  const stock = stocks[pos.instrument_token] || {};
                  return total + calculatePnL(pos, stock);
                }, 0)
              )}`}>
                {netPositions.reduce((total, pos) => {
                  const stock = stocks[pos.instrument_token] || {};
                  return total + calculatePnL(pos, stock);
                }, 0).toLocaleString('en-IN', {minimumFractionDigits: 2})}
              </span> */}
              <span className={`net-pos-summary-value `}>
                {netPositions.reduce((total, pos) => {
                  const stock = stocks[pos.instrument_token] || {};
                  return total + calculatePnL(pos, stock);
                }, 0).toLocaleString('en-IN', {minimumFractionDigits: 2})}
              </span>
            </div>
          </div>
        </div>
        
        <div className="net-pos-header-curve" onClick={handleHeaderToggle}>
          <div className="net-pos-curve-indicator">
            <span className={`net-pos-indicator-arrow ${isHeaderExpanded ? 'expanded' : ''}`}>▼</span>
          </div>
        </div>
      </div>

      {/* White Content Section */}
      <div className="net-pos-content-section">
        {/* Positions Header */}
        <div className="net-pos-positions-header">
          <h2 className="net-pos-positions-title">Positions</h2>
          <div className="net-pos-positions-controls">
            <div className="net-pos-control-icons">
              <span className="net-pos-filter-icon">⋮</span>
              <span className="net-pos-sort-icon">⇅</span>
            </div>
            <button 
              className="net-pos-close-all-btn" 
              onClick={handleCloseAll}
              disabled={isClosingAll || filteredPositions.filter(pos => pos.netQty != 0).length === 0}
            >
              {isClosingAll ? 'Closing...' : 'Close All'}
            </button>
            {/* <button className="net-pos-rollover-btn">Roll Over</button> */}
          </div>
        </div>

        {/* Search Bar */}
        <div className="search-container-netpositions">
          <Search style={{ height: '20px', color: 'var(--text-muted)' }} />
          <input
            type="text"
            id="search-netpositions-input"
            name="search-netpositions"
            placeholder="Search Script"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input-netpositions"
          />
        </div>

        {/* Positions List */}
        <div className="net-pos-positions-list">
          {filteredPositions.length === 0 ? (
            <div className="net-pos-empty-state">
              <div className="net-pos-empty-icon">
                <AccountBalance />
              </div>
              <h3>No Net Positions</h3>
              <p>You don't have any net positions at the moment.</p>
              <button className="net-pos-primary-action-btn" onClick={() => navigate('/')}>
                Start Trading
              </button>
            </div>
          ) : (
            filteredPositions.map((position) => {
              const stock = stocks[position.instrument_token] || {};
              const calculatedPnL = calculatePnL(position, stock);
              return ( 
                <div key={position.positionId} className="net-pos-position-item">
                  <div className="net-pos-position-main">
                    <div className="net-pos-position-left">
                      <div className="net-pos-asset-name">
                        {position.dispname || position.stockName}
                        {position.expiry && (
                          <span className="net-pos-expiry">{position.expiry}</span>
                        )}
                      </div>
                      <div className="net-pos-position-details">
                        {position.netQty != 0 && <span className={`net-pos-side ${position.buyQty > 0 ? 'buy' : 'sell'}`}>
                          {position.buyQty > 0 ? 'BUY' : 'SELL'} ({position.netQty})
                        </span>}
                        <div className="net-pos-trades-link">
                          <Description className="net-pos-trades-icon" onClick={() => handleTradesClick(position)} />
                          <span onClick={() => handleTradesClick(position)}>Trades</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="net-pos-position-right">
                      {position.netQty != 0 && <div className="net-pos-price-range">
                        { position.buyQty ? (Number(position.avgBuyPrice)).toFixed(2) : (Number(position.avgSellPrice)).toFixed(2)} → {position.buyQty > 0 ? (stock.Bid || 0).toFixed(2) : (stock.Ask || 0).toFixed(2)}
                      </div>}
                      {position.netQty == 0 && <div className="net-pos-price-range">
                        <h4 style={{color: 'red', margin: 0}}>Closed</h4>
                      </div>}
                      {position.netQty != 0 && <div className={`net-pos-pnl-value ${getPnLColor(calculatedPnL)}`}>
                        {calculatedPnL.toLocaleString('en-IN', {minimumFractionDigits: 2})}
                      </div>}
                      {position.netQty == 0 && <div className={`net-pos-pnl-value`}>
                        <h4 style={{color: position.realizedPnL >= 0 ? 'green' : 'red', margin: 0}}>{Number(position.realizedPnL).toLocaleString('en-IN', {minimumFractionDigits: 2})}</h4>
                      </div>}
                      {/* <div className="net-pos-trades-link">
                        <Description className="net-pos-trades-icon" />
                        <span>Trades</span>
                      </div> */}
                      {position.netQty != 0 && <div className="net-pos-position-actions">
                        <div className="net-pos-close-position-btn" onClick={() => handleClosePositionClick(position)}>
                          <Close className="net-pos-close-icon" />
                          Close Position
                          {/* {position.expiry && (
                            <span className="net-pos-expiry-date">({new Date(position.expiry).toLocaleDateString('en-GB')})</span>
                          )} */}
                        </div>
                      </div>}
                    </div>
                  </div>
                  
                  
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Close Position Bottom Sheet */}
      {selectedPosition && (
        <BottomSheetClosePosition
          userDetails={userDetails}
          position={selectedPosition}
          stock={stocks[selectedPosition.instrument_token] || {}}
          isOpen={isBottomSheetOpen}
          onClose={handleCloseBottomSheet}
        />
      )}
    </div>
  );
};

export default NetPositions;
